import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { CommandBlock } from "./CommandBlock";
import { SeverityBadge } from "./SeverityBadge";
import { useMode } from "../context/ModeContext";
import { CATEGORY_META } from "../lib/api";
import { toast } from "sonner";
import { Cpu, FileText, Gauge, ListChecks, Loader2, PlayCircle, ExternalLink, ShieldAlert } from "lucide-react";

export const IssueDetailDialog = ({ issue, open, onOpenChange }) => {
  const { proMode } = useMode();
  const [fixing, setFixing] = useState(false);
  const [fixed, setFixed] = useState(false);

  if (!issue) return null;
  const meta = CATEGORY_META[issue.category] || {};

  const runFix = () => {
    setFixing(true);
    setFixed(false);
    setTimeout(() => {
      setFixing(false);
      setFixed(true);
      toast.success(`Automated fix simulated for ${issue.title}`);
    }, 2200);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-border/70 max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="issue-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <span
              className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border"
              style={{ color: meta.color, borderColor: `${meta.color}55`, background: `${meta.color}14` }}
            >
              {issue.category}
            </span>
            <SeverityBadge severity={issue.severity} />
          </div>
          <DialogTitle className="font-heading text-2xl tracking-tight text-left">{issue.title}</DialogTitle>
          <DialogDescription className="sr-only">Diagnostic detail and recommended fix for {issue.title}</DialogDescription>
          <p className="font-mono text-sm text-primary mt-1 text-left">{issue.error_code}</p>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 my-2">
          <Stat icon={Cpu} label="Component" value={issue.component} />
          <Stat icon={Gauge} label="Impact" value={`${issue.impact_score}/100`} />
          <Stat icon={ShieldAlert} label="Severity" value={issue.severity} cap />
        </div>

        <div className="rounded-xl bg-secondary/40 border border-border/60 p-4">
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Root Cause</p>
          <p className="text-sm text-foreground/90 leading-relaxed">{issue.root_cause}</p>
        </div>

        {!proMode ? (
          <div className="mt-1">
            <p className="flex items-center gap-2 text-sm font-semibold mb-3">
              <ListChecks className="h-4 w-4 text-primary" /> Step-by-step fix
            </p>
            <ol className="space-y-2">
              {issue.simple_fix.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm text-foreground/90">
                  <span className="shrink-0 h-6 w-6 rounded-full bg-primary/15 text-primary grid place-items-center text-xs font-bold">
                    {i + 1}
                  </span>
                  <span className="pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        ) : (
          <div className="mt-1 space-y-3">
            <p className="text-sm font-semibold">Automated remediation</p>
            <CommandBlock code={issue.pro_fix} testid="issue-pro-fix" />
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center gap-3 mt-3">
          <Button
            onClick={runFix}
            disabled={fixing}
            className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
            data-testid="run-fix-btn"
          >
            {fixing ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying fix…</>
            ) : fixed ? (
              <>Fix applied ✓ Re-run</>
            ) : (
              <><PlayCircle className="h-4 w-4 mr-2" /> Run Automated Fix</>
            )}
          </Button>
          <a
            href={issue.kb_url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto"
            data-testid="kb-link"
          >
            <Button variant="outline" className="w-full border-border/70">
              <FileText className="h-4 w-4 mr-2" /> {issue.kb_id}
              <ExternalLink className="h-3.5 w-3.5 ml-1.5 opacity-60" />
            </Button>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ icon: Icon, label, value, cap }) => (
  <div className="rounded-xl bg-secondary/40 border border-border/60 p-3">
    <Icon className="h-4 w-4 text-muted-foreground mb-1.5" />
    <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className={`text-sm font-semibold mt-0.5 truncate ${cap ? "capitalize" : ""}`}>{value}</p>
  </div>
);
